import EventEmitter from 'events'

import * as PIXI from 'pixi.js-legacy'
import * as TWEEN from 'tween.js'

import app from '../app'
import { STATES, DBL_CLICK_TIME, MIN_DRAG_DELTA, MAX_DRAG_BINDING_DISTANCE } from '../config'

const DOUBLE_CLICK_EVENT = 'dblclick'
const CLICK_EVENT = 'click'

export default class Shape extends EventEmitter {
  constructor(props = {}) {
    super(props)
    this.setMaxListeners(100)
    if (props.ctx) {
      this.ctx = props.ctx
    } else if (props.isGroup) {
      this.ctx = new PIXI.Container()
    } else {
      this.ctx = new PIXI.Graphics()
    }
    this.isGroup = props.isGroup
    this.ctx.shape = this
    if (Object.prototype.hasOwnProperty.call(props, 'id')) {
      this.ctx.id = this.id = props.id
    }
    if (Object.prototype.hasOwnProperty.call(props, 'x')) {
      this.ctx.x = props.x || 0
    }
    if (Object.prototype.hasOwnProperty.call(props, 'y')) {
      this.ctx.y = props.y || 0
    }

    const { ctx } = this,
      shape = this
    this.position = {
      get x() {
        return ctx.position.x
      },
      set x(value) {
        shape.position.set(value, ctx.y)
      },

      get y() {
        return ctx.position.y
      },
      set y(value) {
        shape.position.set(ctx.x, value)
      },

      set(x, y, noEmitEvent) {
        x = Math.floor(x)
        y = Math.floor(y)
        const { x: currentX, y: currentY } = ctx.position
        if (x === currentX && y === currentY) {
          return
        }

        ctx.position.set(x, y)
        app.render()

        if (noEmitEvent) {
          return
        }
        shape.emit('move')
      },
    }

    this.background = props.background
    this.color = props.color
    this.alpha = props.alpha !== undefined ? props.alpha : 1

    this.strokeWidth = props.strokeWidth || 1
    this.strokeOpacity = props.strokeOpacity || 1
    this.stroke = props.stroke
    this.strokeStyle = props.strokeStyle

    this.radius = props.radius
    this.name = this.ctx.name = props.name
    this.onboardingElementId = this.ctx.onboardingElementId = props.onboardingElementId

    this.pivotX = 0
    this.pivotY = 0
    this.rotation = 0

    this._scale = 1

    this.set('alpha', this.alpha)
    if (Object.prototype.hasOwnProperty.call(props, 'width')) {
      this.setWidth(props.width)
    }
    if (Object.prototype.hasOwnProperty.call(props, 'height')) {
      this.setHeight(props.height)
    }
    if (Object.prototype.hasOwnProperty.call(props, 'interactive')) {
      this.setInteractive(props.interactive)
    }
    if (Object.prototype.hasOwnProperty.call(props, 'visible')) {
      this.ctx.visible = props.visible
    }

    if (this.id) {
      this._isMounted = false

      ctx.on('added', (parent) => {
        let p = this.ctx.parent,
          isMounted = false
        while (p) {
          if (p === app.root.stage) {
            isMounted = true
            break
          }
          p = p.parent
        }

        if (isMounted) {
          const emitChildrenEvent = (children) => {
            for (let child of children) {
              emitChildrenEvent(child.children)
              if (child.id && child.shape) {
                child.shape._emitMounted()
              }
            }
          }
          emitChildrenEvent(this.ctx.children)
          this._emitMounted()
        }
      })

      app.setNode(this)
    }
  }

  _emitMounted = () => {
    if (this._isMounted) {
      return
    }
    this._isMounted = true
    app.emitNodeMounted(this)
  }

  destroy(options = {}) {
    clearTimeout(this.setNodeTimer)
    app.deleteNode(this)

    this.stopFX()

    if (this.parent != null) {
      this.parent.removeChild(this.ctx)
    } else {
      console.warn('[shape.destroy] Parent not find', this.id)
    }

    if (options.children) {
      const children = this.getChildren()
      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i]
        child.shape && child.shape.destroy(options)
      }
    }

    this.emit('destroy')

    this.ctx.destroy()
    app.render()
  }

  // Aliases for position x/y
  get x() {
    return this.position.x
  }
  set x(value) {
    this.position.set(value, this.y)
  }
  get y() {
    return this.position.y
  }
  set y(value) {
    this.position.set(this.x, value)
  }

  get visible() {
    return this.ctx.visible
  }
  set visible(value) {
    this.ctx.visible = value
    app.render()
  }

  get alpha() {
    return this.ctx.alpha
  }
  set alpha(value) {
    this.ctx.alpha = value
    app.render()
  }

  get parent() {
    return this.ctx.parent
  }
  setParent(x) {
    this.ctx.setParent(x.ctx || x)
  }

  get scale() {
    return this._scale
  }
  set scale(val) {
    this._set_scale(val)
  }

  draw() {}

  _set_scale(val) {
    if (this._scale !== val) {
      this._scale = val
      this.ctx.scale.x = val
      this.ctx.scale.y = val
      app.render()
    }
    return this
  }

  _set_pivot(val) {
    if (val.x !== undefined) {
      this.set('pivotX', val.x)
    }
    if (val.y !== undefined) {
      this.set('pivotY', val.y)
    }
    return this
  }

  _set_pivotX(val) {
    if (this.pivotX !== val) {
      this.pivotX = this.ctx.pivot.x = val
    }
  }

  _set_pivotY(val) {
    if (this.pivotY !== val) {
      this.pivotY = this.ctx.pivot.y = val
    }
  }

  _set_rotation(val) {
    if (this.rotation !== val) {
      this.rotation = this.ctx.rotation = val
    }
    return this
  }

  set(props, value) {
    // console.log('props, value: ', props, typeof props)

    if (typeof props === 'object') {
      Object.keys(props).forEach((propName) => {
        this._set(propName, props[propName])
      })
    } else {
      return this._set(props, value)
    }
    return this
  }

  _set(key, value) {
    const setKey = `_set_${key}`
    if (this[setKey]) {
      return this[setKey](value)
    }

    this[key] = this.ctx[key] = value
    app.render()
    // this.emit('set' + key.charAt(0).toUpperCase() + key.slice(1))
    return this
  }

  setInteractive(i) {
    this.ctx.interactive = Boolean(i)
  }

  setHeight = (v) => {
    this._set('height', v)
  }

  setWidth = (v) => {
    this._set('width', v)
  }

  get = (prop) => this.ctx[prop]

  getHeight = () => this.ctx.height

  getWidth = () => this.ctx.width

  getX = () => this.ctx.position.x

  getY = () => this.ctx.position.y

  getChildren = () => this.ctx.children

  setFilters(f) {
    this.ctx.filters = Array.isArray(f) ? f : f ? [f] : f
  }

  moveToFront() {
    const { ctx } = this
    const { parent } = ctx
    if (parent == null) {
      return console.warn(`Cannot move '${this.id}' to front: no parent`)
    }

    parent.setChildIndex(ctx, parent.children.length - 1)
  }

  moveForward() {
    const { ctx } = this
    const { parent } = ctx
    if (parent == null) {
      return console.warn(`Cannot move '${this.id}' to front: no parent`)
    }

    const index = parent.getChildIndex(ctx)
    parent.setChildIndex(ctx, index + 1)
  }

  moveBackward() {
    const { ctx } = this
    const { parent } = ctx
    if (parent == null) {
      return console.warn(`Cannot move '${this.id}' backward: no parent`)
    }

    const index = parent.getChildIndex(ctx)
    parent.setChildIndex(ctx, index - 1)
  }

  moveToBack() {
    const { ctx } = this
    const { parent } = ctx
    if (parent == null) {
      return console.warn(`Cannot move '${this.id}' to back: no parent`)
    }

    parent.setChildIndex(ctx, 0)
  }

  getBounds() {
    return this.ctx.getLocalBounds()
  }

  getAbsolutePosition() {
    return this.ctx.getAbsolutePosition()
  }

  redraw() {
    if (this.isGroup) {
      // this.ctx.removeChildren()
    } else {
      this.ctx.clear()
    }
    app.render()
    this.draw()
    return this
  }

  moveIn = (objects) => {
    objects = Array.isArray(objects) ? objects : [objects]
    objects.forEach((o) => o.setParent(this.ctx))
  }

  // ANIMATION -----------------------------------------------------------------------------

  stopFX() {
    this.tween && this.tween.stop()
    return this
  }

  fx(props, options = { duration: 300 }) {
    const pFrom = Object.keys(props).reduce((acc, propName) => {
      acc[propName] = this[propName]
      return acc
    }, {})

    options = Number.isFinite(options) ? { duration: options } : options

    return this.customFX(pFrom, props, options, (newProps) => {
      this.set(newProps)
      this.redraw()
      if (typeof options === 'function') {
        options(newProps)
      }
    })
  }

  customFX(pFrom, pTo, options, onUpdate = function () {}) {
    options = Number.isFinite(options) ? { duration: options } : options
    this.stopFX()

    this.tween = new TWEEN.Tween(pFrom).to(pTo, options.duration).onUpdate(function () {
      onUpdate(this)
    })
    if (options.easing) {
      this.tween.easing(options.easing)
    }
    return pTo.start !== false ? this.tween.start() : this.tween
  }

  // EVENTS -----------------------------------------------------------------------------

  _handleClickEvent = (e) => {
    if (e.$drag) {
      return
    }

    if (this.listenerCount(DOUBLE_CLICK_EVENT) > 0) {
      const clickInterval = Date.now() - (this.$lastClickTime || 0)
      if (clickInterval < DBL_CLICK_TIME) {
        // Preventing selection
        const { originalEvent } = e.data
        originalEvent.preventDefault && originalEvent.preventDefault()

        return this.emit(DOUBLE_CLICK_EVENT, e)
      }
    }

    this.$lastClickTime = Date.now()
    this.emit('click', e)
  }

  _addClickListener = () => this.ctx.on('click', this._handleClickEvent)
  _removeClickListener = () => this.ctx.off('click', this._handleClickEvent)

  on(type, fn) {
    if (type === 'move' || type === 'destroy') {
      super.on(type, fn)
      return this
    }
    if (type === CLICK_EVENT || type === DOUBLE_CLICK_EVENT) {
      if (this.listenerCount(CLICK_EVENT) === 0 && this.listenerCount(DOUBLE_CLICK_EVENT) === 0) {
        this._addClickListener()
      }
      super.on(type, fn)
      return this
    }
    this.ctx.on(type, fn)
    return this
  }

  off(type, fn) {
    if (type === 'move' || type === 'destroy') {
      super.removeListener(type, fn)
      return this
    }
    if (type === CLICK_EVENT || type === DOUBLE_CLICK_EVENT) {
      super.removeListener(type, fn)
      if (this.listenerCount(CLICK_EVENT) === 0 && this.listenerCount(DOUBLE_CLICK_EVENT) === 0) {
        this._removeClickListener()
      }
      return this
    }
    this.ctx.off(type, fn)
    return this
  }

  draggable = (opts = {}) => {
    if (this._draggableInited) {
      return this
    }

    // const { body } = document
    const shape = this

    this._draggableInited = true
    this.ctx.interactive = true

    if (!opts.filter) {
      opts.filter = (x1, x2, y1, y2) => ({ x: x2, y: y2 })
    }

    let draggedElement = shape

    function onDragStart(e) {
      const { originalEvent } = e.data
      if (originalEvent.shiftKey) {
        return
      }

      if (app.state !== STATES.NORMAL) {
        return
      }

      if (opts.validateTarget) {
        const isValidTarget = opts.validateTarget(e)
        if (!isValidTarget) {
          return
        }
      } else {
        // Preventing drag if event fired for other interactive target
        if (e.target !== e.currentTarget) {
          return
        }
      }

      // Preventing selection
      originalEvent.preventDefault && originalEvent.preventDefault()

      this.handleDragMove = true
      delete e.$drag
      app.blockPanning()

      const { x, y } = this
      // dragPoint - coordinates of pointer relative to dragging object
      this.dragPoint = e.data.getLocalPosition(this.parent)
      this.dragPoint.x -= x
      this.dragPoint.y -= y

      this.startPoint = { ...e.data.global }
      this.startCoordinates = { x, y }
    }

    function onDragMove(e) {
      if (this.handleDragMove) {
        if (!this.dragging) {
          const currentPoint = e.data.global
          const deltaX = Math.abs(this.startPoint.x - currentPoint.x)
          const deltaY = Math.abs(this.startPoint.y - currentPoint.y)

          if (deltaX > MIN_DRAG_DELTA || deltaY > MIN_DRAG_DELTA) {
            if (opts.onDragStart) {
              const onDragStartRes = opts.onDragStart(e, this)
              if (onDragStartRes === false) {
                return
              }
            }
            // Marking global event as drag and also marking this object as currently dragging
            e.$drag = true
            this.dragging = true
            if (app.state === STATES.NORMAL) {
              app.state = STATES.DRAGGING
            }
            // body.classList.add('_flowdragging')

            app.emit('dragging')

            const nodes = app
              .getNodes()
              .filter((n) => app.isNode(n) && n.id !== this.id && n.parent.id !== this.id)
            const { width, height } = this.getBounds(true)
            this.$dragBindings = {
              x: [],
              y: [],
              width: Math.floor(width / app.transform.k),
              height: Math.floor(height / app.transform.k),
            }
            for (let n of nodes) {
              this.$dragBindings.x.push(n.x)
              this.$dragBindings.x.push(
                Math.floor(n.x + n.ctx.getBounds(true).width / app.transform.k),
              )
              this.$dragBindings.y.push(n.y)
              this.$dragBindings.y.push(
                Math.floor(n.y + n.ctx.getBounds(true).height / app.transform.k),
              )
            }

            // Checking altKey for drag avatar
            if (opts.dragAvatarWithAlt && e.data.originalEvent.altKey) {
              const { renderer, stage } = app.root
              const $texture = renderer.generateTexture(
                this,
                PIXI.SCALE_MODES.LINEAR,
                window.devicePixelRatio,
              )

              const $img = new PIXI.Sprite($texture)
              stage.addChild($img)

              const $shape = new Shape({ ctx: $img })
              draggedElement = $shape
            }
          } else {
            return
          }
        }

        const mousePosition = e.data.getLocalPosition(opts.parent || this.parent)
        let newX = mousePosition.x - this.dragPoint.x
        let newY = mousePosition.y - this.dragPoint.y

        // We should not check guides or shift key for non-standard drag situations
        if (app.state === STATES.DRAGGING) {
          if (!opts.noBindings) {
            // Checking bindings to other nodes on stage
            let bindingGuide = { x: null, y: null }
            const { width, height } = this.$dragBindings

            for (let x of this.$dragBindings.x) {
              const x1Binded = Math.abs(x - newX) < MAX_DRAG_BINDING_DISTANCE
              const x2Binded = Math.abs(x - newX - width) < MAX_DRAG_BINDING_DISTANCE
              if (x1Binded || x2Binded) {
                newX = x - (x2Binded ? width : 0)
                bindingGuide.x = app.root.stage.toGlobal(new PIXI.Point(x, 0)).x
                break
              }
            }

            for (let y of this.$dragBindings.y) {
              const y1Binded = Math.abs(y - newY) < MAX_DRAG_BINDING_DISTANCE
              const y2Binded = Math.abs(y - newY - height) < MAX_DRAG_BINDING_DISTANCE
              if (y1Binded || y2Binded) {
                newY = y - (y2Binded ? height : 0)
                bindingGuide.y = app.root.stage.toGlobal(new PIXI.Point(0, y)).y
                break
              }
            }

            app.bindingGuide = bindingGuide
          }

          // Checking shiftKey
          const { originalEvent } = e.data
          if (originalEvent.shiftKey) {
            const deltaX = Math.abs(this.startCoordinates.x - newX)
            const deltaY = Math.abs(this.startCoordinates.y - newY)
            if (deltaX > deltaY) {
              newY = this.startCoordinates.y
            } else {
              newX = this.startCoordinates.x
            }
          }
        }

        opts.onDrag && opts.onDrag(e, newX, newY, this)

        if (opts.noMove) {
          return
        }

        if (this.x !== newX || this.y !== newY) {
          draggedElement.position.set(newX, newY)
        }
      }
    }

    function onDragEnd(e) {
      this.handleDragMove = false
      app.unblockPanning()
      app.bindingGuide = null
      delete this.dragPoint

      if (app.state === STATES.DRAGGING) {
        app.state = STATES.NORMAL
      }

      if (this.dragging) {
        // We can release current object flag now, but event mark will need to be deleted on next mouseDown
        this.dragging = false
        opts.onDragEnd && opts.onDragEnd(e, draggedElement)

        if (draggedElement !== shape) {
          draggedElement.destroy()
          draggedElement = shape
        }
      }
    }

    const onTransform = (transform, prevTransform) => {
      if (!this.ctx.dragging || this.ctx._destroyed) {
        return
      }

      const deltaX = (prevTransform.x - transform.x) / transform.k
      const deltaY = (prevTransform.y - transform.y) / transform.k
      draggedElement.position.set(this.getX() + deltaX, this.getY() + deltaY)
    }

    app.on('transform', onTransform)

    const touchStartHandler = (e) => {
      if (e.data.multitouch) {
        return
      }
      onDragStart.call(this.ctx, e)
    }

    const touchMoveHandler = (e) => {
      if (e.data.multitouch) {
        return
      }
      onDragMove.call(this.ctx, e)
    }

    this.ctx
      .on('mousedown', onDragStart)
      .on('touchstart', touchStartHandler)
      .on('mousemove', onDragMove)
      .on('touchmove', touchMoveHandler)
      .on('mouseup', onDragEnd)
      .on('mouseupoutside', onDragEnd)
      .on('touchend', onDragEnd)
      .on('touchendoutside', onDragEnd)
  }
}
